import { Col, CreateTableState, Grid, NetworkIcon, Typography } from 'gu-ui-kit'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useTranslation } from 'react-i18next'
import styles from 'modules/PartnersProgram/subcomponents/Table/Table.module.scss'
import clsx from 'clsx'
import Dot from 'components/Dot'
import ToggleButton from 'modules/PartnersProgram/subcomponents/Table/subcomponents/ToggleButton'
const NUMBER_VISIBLE_ROWS = 5

export const useTableConfig = (data = [], collapsed) => {
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()
  const [networks, setNetworks] = useState([])
  const NETWORKS = useMemo(
    () =>
      t('reward_calculator::types', {
        returnObjects: true,
      }),
    [t],
  )

  const networksArr = useMemo(
    () =>
      networks
        .slice(0, collapsed ? NUMBER_VISIBLE_ROWS : networks.length)
        .reduce(
          (acc, network, index) => [
            ...acc,
            { ...network, odd: index % 2 === 0 },
            ...(network.active
              ? network.childs.map((n) => ({
                  ...n,
                  odd: index % 2 === 0,
                  parentNetwork: network.network,
                }))
              : []),
          ],
          [],
        ),
    [networks, NUMBER_VISIBLE_ROWS, collapsed],
  )

  useEffect(() => {
    setNetworks(data)
  }, [data])

  const toggleActiveNetwork = useCallback(
    (network) =>
      setNetworks((networks) =>
        networks.map((n) =>
          n.network === network ? { ...n, active: !n.active } : n,
        ),
      ),
    [],
  )

  const tableConfig = useMemo(
    () => ({
      columns: [
        {
          id: 'network',
          className: clsx(styles['table__table-cell'], 'margin-bottom-16'),
          header: (
            <Typography
              component={'span'}
              variant={
                breakpoint.mds
                  ? 'Body-10'
                  : breakpoint.xls
                  ? 'Body-15'
                  : 'Body-20'
              }
              color={'Label-Secondary'}
            >
              {t('partners_program::table.head.network')}
            </Typography>
          ),
          cell: ({
            row: {
              original: { network, parentNetwork, childs, active, odd } = {},
            },
          }) => (
            <Grid
              className={odd ? 'odd' : ''}
              alignItems={'center'}
              gap={8}
              wrap={false}
            >
              <Col flexGrow={0} flexShrink={0}>
                {NETWORKS?.[network]?.icon && (
                  <NetworkIcon
                    name={NETWORKS?.[network]?.icon}
                    rounded
                    width={24}
                  />
                )}
              </Col>
              <ToggleButton
                hasChild={childs?.length > 0}
                network={network}
                active={active}
                toggleActiveNetwork={toggleActiveNetwork}
              >
                <Typography
                  component={'span'}
                  variant={
                    breakpoint.fbs
                      ? 'Body-10'
                      : breakpoint.mds
                      ? 'Body-15'
                      : 'Body-20'
                  }
                  color={'Label-Fifth'}
                  className={clsx({
                    'padding-left-48': parentNetwork,
                  })}
                >
                  {t(NETWORKS?.[network]?.label)}
                </Typography>
              </ToggleButton>
            </Grid>
          ),
        },
        {
          className: styles['table__table-cell'],
          id: '30000-499999',
          header: (
            <Typography
              component={'span'}
              variant={
                breakpoint.mds
                  ? 'Body-10'
                  : breakpoint.xls
                  ? 'Body-15'
                  : 'Body-20'
              }
              color={'Label-Secondary'}
            >
              {t('partners_program::table.head.from')} 30 000{' '}
              {t('partners_program::table.head.to')} 499 999 ₽
            </Typography>
          ),
          cell: ({ row: { original: { rates = {} } = {} } }) => {
            const [{ commission_percent = 0, award_percent = 0 }] = rates
            return (
              <Grid alignItems={'center'} justifyContent={'center'}>
                <Dot
                  touched={award_percent > 0}
                  error={award_percent === 0}
                  size={'s'}
                />
                <Grid justifyContent={'center'} style={{ width: 50 }}>
                  <Typography
                    component={'span'}
                    variant={
                      breakpoint.fbs
                        ? 'Body-10'
                        : breakpoint.mds
                        ? 'Body-15'
                        : 'Body-20'
                    }
                    color={'Label-Fifth'}
                  >
                    {award_percent > 0
                      ? award_percent
                      : commission_percent > 0
                      ? '-' + commission_percent
                      : '0'}
                    %
                  </Typography>
                </Grid>
              </Grid>
            )
          },
        },
        {
          id: '500000-2999999',
          className: styles['table__table-cell'],
          header: (
            <Typography
              component={'span'}
              variant={
                breakpoint.mds
                  ? 'Body-10'
                  : breakpoint.xls
                  ? 'Body-15'
                  : 'Body-20'
              }
              color={'Label-Secondary'}
            >
              {t('partners_program::table.head.from')} 500 000{' '}
              {t('partners_program::table.head.to')} 2 999 999 ₽
            </Typography>
          ),
          cell: ({ row: { original: { rates = {} } = {} } }) => {
            const [, { commission_percent = 0, award_percent = 0 }] = rates
            return (
              <Grid alignItems={'center'} justifyContent={'center'}>
                <Dot
                  touched={award_percent > 0}
                  error={award_percent === 0}
                  size={'s'}
                />
                <Grid justifyContent={'center'} style={{ width: 50 }}>
                  <Typography
                    component={'span'}
                    variant={
                      breakpoint.fbs
                        ? 'Body-10'
                        : breakpoint.mds
                        ? 'Body-15'
                        : 'Body-20'
                    }
                    color={'Label-Fifth'}
                  >
                    {award_percent > 0
                      ? award_percent
                      : commission_percent > 0
                      ? '-' + commission_percent
                      : '0'}
                    %
                  </Typography>
                </Grid>
              </Grid>
            )
          },
        },
        {
          id: '3000000',
          className: styles['table__table-cell'],
          header: (
            <Typography
              component={'span'}
              variant={
                breakpoint.mds
                  ? 'Body-10'
                  : breakpoint.xls
                  ? 'Body-15'
                  : 'Body-20'
              }
              color={'Label-Secondary'}
            >
              {t('partners_program::table.head.from')} 3 000 000 ₽
            </Typography>
          ),
          cell: ({ row: { original: { rates = {} } = {} } }) => {
            const [, , { commission_percent = 0, award_percent = 0 }] = rates
            return (
              <Grid alignItems={'center'} justifyContent={'center'}>
                <Dot
                  touched={award_percent > 0}
                  error={award_percent === 0}
                  size={'s'}
                />
                <Grid justifyContent={'center'} style={{ width: 50 }}>
                  <Typography
                    component={'span'}
                    variant={
                      breakpoint.fbs
                        ? 'Body-10'
                        : breakpoint.mds
                        ? 'Body-15'
                        : 'Body-20'
                    }
                    color={'Label-Fifth'}
                  >
                    {award_percent > 0
                      ? award_percent
                      : commission_percent > 0
                      ? '-' + commission_percent
                      : '0'}
                    %
                  </Typography>
                </Grid>
              </Grid>
            )
          },
        },
      ],
      state: {},
      showHeader: networksArr?.length > 0,
      showFooter: false,
      className: styles['table'],
      skin: 'compact',
    }),
    [networksArr, breakpoint],
  )

  return {
    tableState: CreateTableState(tableConfig, networksArr),
    tableConfig,
  }
}
